import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SearchDropdown } from '@/components/SearchDropdown';

export interface PeopleFilterState {
  search: string;
  buyingIntent: string[];
  qualificationStatus: string[];
  datePreset: string;
}

interface PeopleFiltersProps {
  filters: PeopleFilterState;
  onFiltersChange: (patch: Partial<PeopleFilterState>) => void;
  onReset: () => void;
  onExportCsv: () => void;
  isExporting?: boolean;
}

const intentOptions = [
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const qualificationOptions = [
  { label: 'Qualified', value: 'qualified' },
  { label: 'Unqualified', value: 'unqualified' },
  { label: 'Unknown', value: 'unknown' },
];

const datePresetOptions = [
  { label: 'Last 7 Days', value: 'last_7_days' },
  { label: 'Last 30 Days', value: 'last_30_days' },
  { label: 'Last 90 Days', value: 'last_90_days' },
  { label: 'All Time', value: 'all_time' },
];

function SingleSelectFilter({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 shrink-0 justify-between rounded-md border border-border bg-background px-3 text-sm text-muted-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
        >
          <span className="truncate text-left">{current?.label ?? label}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-2" align="start">
        <div className="flex flex-col gap-1">
          {value && (
            <button
              type="button"
              className="flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm text-left text-muted-foreground hover:bg-accent"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              All {label}
            </button>
          )}
          {options.map(({ label: optLabel, value: optValue }) => (
            <button
              key={optValue}
              type="button"
              className={cn(
                'flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent',
                value === optValue && 'font-medium bg-accent'
              )}
              onClick={() => {
                onChange(optValue);
                setOpen(false);
              }}
            >
              {optLabel}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { label: string; value: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const triggerLabel =
    selected.length === 0
      ? `All ${label}`
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
        : `${label} (${selected.length})`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 shrink-0 justify-between rounded-md border border-border bg-background px-3 text-sm text-muted-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-2" align="start">
        <div className="flex flex-col gap-1">
          {options.map(({ label: optLabel, value }) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
            >
              <Checkbox
                checked={selected.includes(value)}
                onCheckedChange={() => toggle(value)}
              />
              {optLabel}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const PeopleFilters: React.FC<PeopleFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
  onExportCsv,
  isExporting,
}) => {
  const hasActiveFilters =
    filters.search.length > 0 ||
    filters.buyingIntent.length > 0 ||
    filters.qualificationStatus.length > 0 ||
    filters.datePreset !== 'last_30_days';

  return (
    <div className="flex w-full items-center gap-2 sm:gap-3 overflow-x-auto rounded-lg border border-border bg-secondary/50 px-3 py-2">
      <span className="text-xs font-semibold text-subheading uppercase tracking-wide">Filters</span>

      <div className="hidden h-4 w-px bg-border sm:block" />

      <SingleSelectFilter
        label="Last 30 Days"
        options={datePresetOptions}
        value={filters.datePreset}
        onChange={(value) => onFiltersChange({ datePreset: value || 'last_30_days' })}
      />

      <MultiSelectFilter
        label="Intent"
        options={intentOptions}
        selected={filters.buyingIntent}
        onChange={(values) => onFiltersChange({ buyingIntent: values })}
      />

      <MultiSelectFilter
        label="Qualification"
        options={qualificationOptions}
        selected={filters.qualificationStatus}
        onChange={(values) => onFiltersChange({ qualificationStatus: values })}
      />

      <div className="shrink-0">
        <SearchDropdown
          value={filters.search}
          onChange={(value) => onFiltersChange({ search: value })}
          placeholder="Search email..."
          buttonClassName="h-8 rounded-md border border-border bg-background px-3 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          iconClassName="text-icon"
        />
      </div>

      <div className="ml-auto flex items-center gap-2">
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground hover:text-heading" onClick={onReset}>
            Clear
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          className="h-8 rounded-md px-3 text-sm"
          onClick={onExportCsv}
          disabled={isExporting}
        >
          <Download className="mr-1.5 h-3.5 w-3.5" />
          {isExporting ? 'Exporting...' : 'CSV'}
        </Button>
      </div>
    </div>
  );
};

export default PeopleFilters;
